import { useState } from "react";
import { motion } from "framer-motion";
import { Briefcase, GraduationCap, Factory, PartyPopper, Trophy, Users2, Building2, School, Landmark, Users, Heart, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import SEO from "../lib/seo";
import PageHero from "../components/PageHero";
import SectionHeading from "../components/SectionHeading";
import CtaSection from "../components/CtaSection";
import { Reveal } from "../components/Reveal";
import { postInquiry } from "../lib/api";
import { useSettings } from "../lib/store";
import { track } from "../lib/track";
import { waLink, MSG } from "../lib/whatsapp";
import { WhatsAppIcon } from "../components/icons";

const HERO_IMG =
  "https://images.unsplash.com/photo-1651467606797-e1c660cf3fda?crop=entropy&cs=srgb&fm=jpg&q=80&auto=format&fit=crop&w=1800";

const TARGETS = [
  { icon: Building2, label: "Corporate" },
  { icon: Briefcase, label: "Company" },
  { icon: School, label: "School" },
  { icon: GraduationCap, label: "University" },
  { icon: Users, label: "Community" },
  { icon: Landmark, label: "Organization" },
  { icon: Landmark, label: "Government / Institution" },
];

const SERVICES = [
  { icon: Users2, title: "Group Land Arrangement", desc: "Pengaturan penuh perjalanan group di negara tujuan: bus, hotel, guide, dan program." },
  { icon: GraduationCap, title: "Study Tour", desc: "Perjalanan edukatif untuk pelajar dan mahasiswa dengan kunjungan terkurasi." },
  { icon: PartyPopper, title: "Company Gathering", desc: "Gathering perusahaan yang seru dan tertata, domestik maupun internasional." },
  { icon: Trophy, title: "Incentive Trip", desc: "Apresiasi untuk tim dan top performer dengan destinasi yang memotivasi." },
  { icon: Briefcase, title: "Business Trip", desc: "Perjalanan bisnis yang efisien dengan transportasi dan jadwal yang rapi." },
  { icon: Factory, title: "Industry Visit", desc: "Kunjungan industri dan pabrik dengan koordinasi menyeluruh." },
];

export default function CorporateGroup() {
  const [form, setForm] = useState({ name: "", organization: "", type: "", pax: "", destination: "", date: "", whatsapp: "", notes: "" });
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);
  const { settings } = useSettings();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.whatsapp.trim() || !form.organization.trim()) {
      toast.error("Mohon isi nama, organisasi, dan nomor WhatsApp.");
      return;
    }
    setSending(true);
    try {
      await postInquiry({ type: "group", ...form, source: "corporate-group-page" });
      track("submit_group_inquiry", { destination: form.destination });
      setDone(true);
    } catch {
      toast.error("Gagal mengirim. Silakan coba lagi.");
    } finally {
      setSending(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById("group-form")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <SEO
        title="Corporate & Group Travel — Group Land Arrangement | Beyond Travel"
        description="Group Land Arrangement untuk corporate, school, university, community, dan organization: study tour, company gathering, incentive trip, hingga industry visit."
      />
      <PageHero
        image={HERO_IMG}
        eyebrow="Corporate & Group"
        title="Corporate & Group Travel"
        sub="Perjalanan group untuk perusahaan, sekolah, universitas, komunitas, dan organisasi — dengan penawaran khusus dan pendampingan penuh dari tim kami."
        crumb="Corporate & Group"
      >
        <button onClick={scrollToForm} className="btn btn-gold !px-8 !py-3.5" data-testid="corporate-request-proposal-cta">
          Request Group Proposal
        </button>
        <a href={waLink(settings, MSG.group)} target="_blank" rel="noreferrer" className="btn btn-wa !px-8 !py-3.5" data-testid="corporate-whatsapp-cta" onClick={() => track("click_whatsapp", { source: "corporate_hero" })}>
          <WhatsAppIcon className="h-5 w-5" /> WhatsApp
        </a>
      </PageHero>

      <section className="section bg-white">
        <div className="container-x">
          <SectionHeading eyebrow="Siapa yang Kami Layani" title="Untuk Semua Tipe Group" align="center" />
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            {TARGETS.map((t, i) => (
              <Reveal key={t.label} delay={Math.min(i * 0.05, 0.3)}>
                <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-cream px-5 py-2.5 text-sm font-semibold text-charcoal shadow-crystal transition hover:border-tosca-600 hover:text-tosca-700">
                  <t.icon className="h-4 w-4 text-tosca-600" /> {t.label}
                </span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-cream" data-testid="corporate-services">
        <div className="container-x">
          <SectionHeading
            eyebrow="Layanan Group"
            title="Satu Partner, Semua Program Group"
            sub="Dari study tour pelajar hingga incentive trip perusahaan — kami tangani seluruh detailnya."
          />
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => (
              <Reveal key={s.title} delay={Math.min(i * 0.07, 0.3)} className="h-full">
                <div className="h-full rounded-3xl bg-white p-6 shadow-crystal card-hover" data-testid={`corporate-service-${s.title.toLowerCase().replace(/ /g, "-")}`}>
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-tosca-600 text-gold">
                    <s.icon className="h-6 w-6" />
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-bold text-charcoal">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-500">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-white" id="group-form" data-testid="group-inquiry-form">
        <div className="container-x max-w-2xl">
          <SectionHeading
            eyebrow="Request Group Proposal"
            title="Ceritakan Program Group Anda"
            sub="Isi form singkat ini, tim kami akan menyiapkan proposal dan menghubungi Anda."
            align="center"
          />
          {done ? (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="mt-8 rounded-3xl bg-cream p-8 text-center shadow-crystal" data-testid="group-inquiry-success">
              <CheckCircle2 className="mx-auto h-12 w-12 text-tosca-600" />
              <h3 className="mt-4 font-heading text-xl font-bold text-charcoal">Proposal Request Terkirim</h3>
              <p className="mt-2 text-sm text-slate-500">Terima kasih. Tim Beyond Travel akan menghubungi Anda untuk membahas program group Anda.</p>
              <a href={waLink(settings, MSG.group)} target="_blank" rel="noreferrer" className="btn btn-wa mt-6" data-testid="group-inquiry-success-whatsapp">
                <WhatsAppIcon className="h-5 w-5" /> Percepat via WhatsApp
              </a>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="mt-8 space-y-4 rounded-3xl border border-slate-100 bg-cream p-6 shadow-crystal sm:p-8">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="label">Nama PIC*</label>
                  <input className="field" placeholder="Nama Anda" value={form.name} onChange={set("name")} data-testid="group-form-name-input" />
                </div>
                <div>
                  <label className="label">Organisasi / Instansi*</label>
                  <input className="field" placeholder="cth. PT Maju Jaya / SMA X" value={form.organization} onChange={set("organization")} data-testid="group-form-organization-input" />
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                <div>
                  <label className="label">Tipe Group</label>
                  <select className="field" value={form.type} onChange={set("type")} data-testid="group-form-type-select">
                    <option value="">Pilih tipe</option>
                    {["Corporate", "School", "University", "Community", "Organization", "Government / Institution", "Family Group"].map((t) => (
                      <option key={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="label">Jumlah Peserta</label>
                  <input className="field" placeholder="cth. 30" value={form.pax} onChange={set("pax")} data-testid="group-form-pax-input" />
                </div>
                <div>
                  <label className="label">Target Tanggal</label>
                  <input type="date" className="field" value={form.date} onChange={set("date")} data-testid="group-form-date-input" />
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="label">Destinasi</label>
                  <input className="field" placeholder="cth. China / Malaysia / fleksibel" value={form.destination} onChange={set("destination")} data-testid="group-form-destination-input" />
                </div>
                <div>
                  <label className="label">No. WhatsApp*</label>
                  <input className="field" placeholder="08xxxxxxxxxx" value={form.whatsapp} onChange={set("whatsapp")} data-testid="group-form-whatsapp-input" />
                </div>
              </div>
              <div>
                <label className="label">Catatan Program</label>
                <textarea className="field min-h-24" placeholder="Kebutuhan khusus program, budget indikasi, dll." value={form.notes} onChange={set("notes")} data-testid="group-form-notes-input" />
              </div>
              <button type="submit" disabled={sending} className="btn btn-primary w-full !py-3.5" data-testid="group-form-submit-button">
                {sending ? "Mengirim..." : "Request Group Proposal"}
              </button>
            </form>
          )}
        </div>
      </section>

      <CtaSection title="Group Anda Siap Berangkat?" sub="Diskusikan program group Anda dengan tim kami sekarang." testid="cta-corporate" />
    </>
  );
}
